import { ElementRef, Injectable, QueryList } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, isFormArray, isFormControl, isFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { StorageType } from '../../core/interface/storage-type';
import { ApiService } from '../../core/services/api.service';
import { ApiConstant } from '../../core/constants/api-constants';
import { LoaderService } from '../../core/services/loader.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import saveAs from 'file-saver';
import printJS from 'print-js';
import { ToasterService } from '../../core/services/toaster.service';
import { APP_CONSTANTS, DEFAULT_PAYLOAD_VALUES } from '../../core/constants/app-constants';
import { InventoryLayoutJSON } from '../../core/constants/inventory-layout-json';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class SharedService {

  inventoryTypeIds: Record<string, number> = {};

  constructor(
    private apiService: ApiService,
    private loaderService: LoaderService,
    private http: HttpClient,
    private toasterService: ToasterService,
  ) { }

  public addOrRemoveRequiredValidator(form: FormGroup, controlNameArry: string[], shouldAdd: boolean): void {
    controlNameArry.forEach((controlName) => {
      const control = form.get(controlName);
      if (control) {
        if (shouldAdd) {
          control.addValidators(Validators.required);
          control.enable({ emitEvent: false });
        } else {
          control.removeValidators(Validators.required);
          control.disable({ emitEvent: false });
        }
        control.updateValueAndValidity();
      }
    });
  }

  clearRequiredValidation(control: FormControl) {
    const currentValidators = control.validator ? [control.validator] : [];
    const filteredValidations = currentValidators.filter((validator: ValidatorFn) => validator !== Validators.required);
    control.clearValidators();
    control.setValidators(filteredValidations);
  }

  public setPayloadDateFormat(str: string) {
    if (str) {
      str = str.split('/').reverse().join('-');
    }
    return str;
  }

  public transformpayloadDate(str: string) {
    if (str) {
      str = str.split('-').reverse().join('/');
    }
    return str;
  }

  // public download(base64String: string | null, file_name: string,contentType:string = "application/pdf") {
  //       // with metadata prefix


  public download(inputString: string | null, fileName: string, contentType: string = "") {
    if (!inputString) return;

    // 1. Auto-detect content type if not provided
    if (!contentType) {
      contentType = this.getContentTypeFromExtension(fileName);
    }

    const isS3Url = inputString.includes(APP_CONSTANTS.S3_UPLOAD);

    if (isS3Url) {
      // ... (Your existing S3 logic) ...
      this.http.get(inputString, { responseType: 'blob' }).subscribe({
        next: (blob: Blob) => {
          // Create a new blob with the correct type if the server response type was generic
          const fileBlob = new Blob([blob], { type: contentType });
          this.triggerDownload(fileBlob, fileName);
        },
        error: (err: any) => console.error('Download failed', err)
      });
    } else {
      // SCENARIO 2: Base64
      const blob = this.base64ToBlob(inputString, contentType);
      this.triggerDownload(blob, fileName);
    }
  }

  // Helper to determine MIME type
  private getContentTypeFromExtension(fileName: string): string {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'xlsx':
        return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      case 'xls':
        return 'application/vnd.ms-excel';
      case 'pdf':
        return 'application/pdf';
      case 'png':
        return 'image/png';
      case 'jpg':
      case 'jpeg':
        return 'image/jpeg';
      default:
        return 'application/octet-stream';
    }
  }

  // Your existing helpers...
  private base64ToBlob(base64: string, contentType: string): Blob {
    const base64Data = base64.includes(',') ? base64.split(',')[1] : base64;
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: contentType });
  }

  private triggerDownload(blob: Blob, fileName: string) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }

  public transformFormData(data: any) {
    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        const item = data[key];
        if (item && typeof item == 'object' && item.hasOwnProperty('id')) {
          if (item.id === 0 || item.id === null) {
            data[key] = { id: 0, value: '' }; // change the value of the key
          }
        }
        if (
          key === 'quantity_per_tube' ||
          key === 'volume' ||
          key === 'concentration' ||
          key === 'volume_per_vial' ||
          key === 'tot_cell_count' ||
          key === 'lab_equip_inv_storage' ||
          key === 'lab_equip_inv_no_of_boxes'
        ) {
          if (item === '' || item === undefined) {
            data[key] = null;
          }
        }
      }
    }
    return data;
  }

  public deepCopy(data: any) {
    try {
      return JSON.parse(JSON.stringify(data));
    } catch (error) {
      return data;
    }
  }

  compareObjects(firstObj: any, secondObj: any) {
    let isEqualObjects = true;
    if (firstObj && secondObj) {
      if (Object.keys(firstObj).length !== Object.keys(secondObj).length) {
        isEqualObjects = false;
        return false;
      }
      for (const property in firstObj) {
        if (property) {
          if (firstObj.hasOwnProperty(property) !== secondObj.hasOwnProperty(property)) {
            isEqualObjects = false;
            return false;
          }
          switch (typeof firstObj[property]) {
            case 'object':
              if (!this.compareObjects(firstObj[property], secondObj[property])) {
                isEqualObjects = false;
                return false;
              }
              break;
            case 'function':
              break;
            default:
              if ((firstObj[property] || secondObj[property]) && firstObj[property] !== secondObj[property]) {
                isEqualObjects = false;
                return false;
              }
          }
        }
      }
      return isEqualObjects;
    } else if (!firstObj && !secondObj) {
      return true;
    } else {
      return false;
    }
  }

  transformJSON(data: any[]) {
    const assignStorage: any = {};
    if (data?.length) {
      data.forEach((d) => {
        if (d.box?.length) {
          d.box.forEach((b: any) => {
            if (b.selected?.length) {
              b.selected.forEach((s: any) => {
                const storage = {
                  grid_id: s.grid_id || null,
                  h_pos: s.h_pos,
                  v_pos: s.v_pos,
                  storage_id: d.storage_id,
                  box_no: b.box_no,
                  shelf: b.shelf,
                  rack: b.rack,
                  drawer: b?.drawer,

                };
                if (s.item_details?.id) {
                  if (assignStorage[s.item_details.id]) {
                    assignStorage[s.item_details.id].push(storage);
                  } else {
                    assignStorage[s.item_details.id] = [storage];
                  }
                }
              });
            }
          });
        }
      });
    }
    return assignStorage;
  }

  // sort stoarge by combining same datas
  sortSelectedStorageArray(storageArray: StorageType[]) {
    const sortedArray: StorageType[] = [];
    storageArray.forEach((storage) => {
      const sortedStorage = sortedArray.find((str) => str.storage_id === storage.storage_id);
      if (sortedStorage) {
        storage.box.forEach((box) => {
          const sortedBox = sortedStorage.box.find((sbox) => sbox.box_id == box.box_id);
          if (sortedBox) {
            box.selected?.forEach((selectedItem) => {
              const sortSelected = sortedBox.selected?.find(
                (sItem) => sItem.h_pos == selectedItem.h_pos && sItem.v_pos == selectedItem.v_pos,
              );
              if (!sortSelected) {
                sortedBox.selected?.push(selectedItem);
              }
            });
          } else {
            sortedStorage.box.push(box);
          }
        });
      } else {
        sortedArray.push(storage);
      }
    });
    return sortedArray;
  }

  buildFormGroup(controls: any) {
    const formGroup = new FormGroup({});
    for (const key in controls) {
      if (Object.prototype.hasOwnProperty.call(controls, key)) {
        formGroup.addControl(key, new FormControl(controls[key]));
      }
    }
    return formGroup;
  }

  downloadpdfPrint(key: string, id: any, source: string) {
    let endpoint;
    switch (source) {
      case 'project':
        endpoint = this.apiService.getApiUrl(ApiConstant.PROJECTS.DOWNLOAD_PDF + `/${id}`) as any;
        break;
      case 'plasmid':
        endpoint = this.apiService.getApiUrl(
          ApiConstant.INVENTORY.BIOLOGICAL_SAMPLE_INVENTORY.PLASMIDS.INVENTORY.DOWNLOAD_REPORT + `/${id}`,
        ) as any;
        break;
      case 'bacterial_starin':
        endpoint = this.apiService.getApiUrl(
          ApiConstant.INVENTORY.BIOLOGICAL_SAMPLE_INVENTORY.BACTERIAL_STRAINS.INVENTORY.DOWNLOAD_REPORT + `/${id}`,
        ) as any;
        break;
      case 'cell_line':
        endpoint = this.apiService.getApiUrl(
          ApiConstant.INVENTORY.BIOLOGICAL_SAMPLE_INVENTORY.CELL_LINES.INVENTORY.DOWNLOAD_REPORT + `/${id}`,
        ) as any;
        break;
      case 'freezer':
        endpoint = this.apiService.getApiUrl(
          ApiConstant.INVENTORY.LAB_EQUIPMENTS.FREEZER.DOWNLOAD_REPORT + `/${id}`,
        ) as any;
        break;
      case 'refrigerator':
        endpoint = this.apiService.getApiUrl(
          ApiConstant.INVENTORY.LAB_EQUIPMENTS.REFRIGERATOR.DOWNLOAD_REPORT + `/${id}`,
        ) as any;
        break;
      case 'storage-cabinet':
        endpoint = this.apiService.getApiUrl(
          ApiConstant.INVENTORY.LAB_EQUIPMENTS.STORAGE_CABINETS_AND_SHELVES.DOWNLOAD_REPORT_SC + `/${id}`,
        ) as any;
        break;
      case 'shelves':
        endpoint = this.apiService.getApiUrl(
          ApiConstant.INVENTORY.LAB_EQUIPMENTS.STORAGE_CABINETS_AND_SHELVES.DOWNLOAD_REPORT_SHELVES + `/${id}`,
        ) as any;
        break;
      case 'assay-kits':
        endpoint = this.apiService.getApiUrl(
          ApiConstant.INVENTORY.LAB_CONSUMABLES.DOWNLOAD_REPORT.ASSAYKITS + `/${id}`,
        ) as any;
        break;
      case 'plasticwares':
        endpoint = this.apiService.getApiUrl(
          ApiConstant.INVENTORY.LAB_CONSUMABLES.DOWNLOAD_REPORT.PLASTICWARE + `/${id}`,
        ) as any;
        break;
      case 'glasswares':
        endpoint = this.apiService.getApiUrl(
          ApiConstant.INVENTORY.LAB_CONSUMABLES.DOWNLOAD_REPORT.GLASSWARE + `/${id}`,
        ) as any;
        break;
      case 'agar-plates':
        endpoint = this.apiService.getApiUrl(
          ApiConstant.INVENTORY.LAB_CONSUMABLES.DOWNLOAD_REPORT.AGARPLATES + `/${id}`,
        ) as any;
        break;
      case 'cell-culture-media':
        endpoint = this.apiService.getApiUrl(
          ApiConstant.INVENTORY.LAB_CONSUMABLES.DOWNLOAD_REPORT.CELLCULTUREMEDIA + `/${id}`,
        ) as any;
        break;
      case 'antibiotics':
        endpoint = this.apiService.getApiUrl(
          ApiConstant.INVENTORY.LAB_CONSUMABLES.DOWNLOAD_REPORT.ANTIBIOTICS + `/${id}`,
        ) as any;
        break;
      case 'gels':
        endpoint = this.apiService.getApiUrl(
          ApiConstant.INVENTORY.LAB_CONSUMABLES.DOWNLOAD_REPORT.GELS + `/${id}`,
        ) as any;
        break;
      case 'buffers':
        endpoint = this.apiService.getApiUrl(
          ApiConstant.INVENTORY.LAB_CONSUMABLES.DOWNLOAD_REPORT.BUFFERS + `/${id}`,
        ) as any;
        break;
      case 'lab-reagents':
        endpoint = this.apiService.getApiUrl(
          ApiConstant.INVENTORY.LAB_CONSUMABLES.DOWNLOAD_REPORT.LABREAGENTS + `/${id}`,
        ) as any;
        break;
      case 'instruments':
        endpoint = this.apiService.getApiUrl(
          ApiConstant.INVENTORY.INSTRUMENTS.INVENTORY.DOWNLOAD_REPORT + `/${id}`,
        ) as any;
        break;
      case 'protocol':
        endpoint = this.apiService.getApiUrl(ApiConstant.PROTOCOLS.DOWNLOAD + `/${id}`) as any;
        break;
    }
    this.loaderService.displayLoader(true);
    this.http.post(endpoint, null, { responseType: 'blob' }).subscribe({
      next: (responseBlob: Blob) => {
        this.loaderService.displayLoader(false);
        if (responseBlob) {
          if (key === 'download') {
            let filename;
            switch (source) {
              case 'project':
                filename = `project_summary_report_${new Date().getTime()}.pdf`;
                break;
              case 'plasmid':
              case 'bacterial_starin':
              case 'cell_line':
              case 'freezer':
              case 'storage-cabinet':
              case 'refrigerator':
              case 'shelves':
              case 'assay-kits':
              case 'plasticwares':
              case 'glasswares':
              case 'agar-plates':
              case 'cell-culture-media':
              case 'antibiotics':
              case 'gels':
              case 'buffers':
              case 'lab-reagents':
              case 'instruments':
              case 'protocol':
                filename = `${source}_inventory_${new Date().getTime()}.pdf`;
                break;
            }
            saveAs(responseBlob, filename);
          } else {
            const blobUrl = URL.createObjectURL(responseBlob);
            printJS(blobUrl);
          }
        } else {
          this.toasterService.alert.toast({
            type: 'error',
            message: 'error',
          });
        }
      },
      error: (error: any) => {
        this.toasterService.alert.toast({
          type: 'error',
          message: error.message,
        });
        this.loaderService.displayLoader(false);
      },
    });
  }

  //productized inventory item print/download
  downloadPrintInvPDF(key: string, data: { type: number | string | null; item_id: number;loc_code?: string; }, source: string, isMaster: boolean = false) {
    let url = ApiConstant.INVENTORY.DOWNLOAD.ENDPOINT;
    if (isMaster) {
      url = ApiConstant.INVENTORY.DOWNLOAD.MASTER_ENDPOINT;
    }
    url = this.updateUrlPlaceHolders(url, data);
    const endpoint = this.apiService.getApiUrl(url);
    this.loaderService.displayLoader(true);
    this.http.post(endpoint, null, { responseType: 'blob' }).subscribe({
      next: (responseBlob: Blob) => {
        this.loaderService.displayLoader(false);
        if (responseBlob) {
          if (key === 'download') {
            let filename;
            if (source === 'project') {
              filename = `project_summary_report_${new Date().getTime()}.pdf`;
            } else {
              filename = source;
            }
            saveAs(responseBlob, filename);
          } else {
            const blobUrl = URL.createObjectURL(responseBlob);
            printJS(blobUrl);
          }
        } else {
          this.toasterService.alert.toast({
            type: 'error',
            message: 'error',
          });
        }
      },
      error: (error: any) => {
        this.toasterService.alert.toast({
          type: 'error',
          message: error.message,
        });
        this.loaderService.displayLoader(false);
      },
    });
  }

  cloneFormControls(control: any) {
    let newControl: any;
    if (isFormGroup(control)) {
      const formGroup = new FormGroup({}, control.validator, control.asyncValidator);
      const controls = control.controls;
      Object.keys(controls).forEach((key) => {
        formGroup.addControl(key, this.cloneFormControls(controls[key]));
      });
      newControl = formGroup as any;
    } else if (isFormArray(control)) {
      const formArray: any = new FormArray([], control.validator, control.asyncValidator);
      control.controls.forEach((formControl) => formArray.push(this.cloneFormControls(formControl)));
      newControl = formArray as any;
    } else if (isFormControl(control)) {
      newControl = new FormControl(control.value, control.validator, control.asyncValidator) as any;
    } else {
      throw new Error('Error: unexpected control value');
    }

    if (control.disabled) {
      newControl.disable({ emitEvent: false });
    }

    return newControl;
  }

  base64ToArrayBuffer(base64: any): ArrayBuffer {
    const binary_string = window.atob(base64);
    const len = binary_string.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binary_string.charCodeAt(i);
    }
    return bytes.buffer;
  }

  decodeBase64String(base64String: string) {
    const decodedString = atob(base64String);
    const prefixRegex = /^data:([^;]*);base64,/;
    const match = decodedString.match(prefixRegex);
    if (match) {
      // Remove the prefix part from the decoded string
      const base64Data = decodedString.replace(prefixRegex, '');
      return base64Data; // This is the clean base64 string without the encoded prefix
    }
    // If no prefix is found, return the decoded string as-is
    return base64String;
  }

  checkMetaDataPrefix(base64String: string) {
    const decodedString = atob(base64String);
    const prefixRegex = /^data:([^;]*);base64,/;
    const match = decodedString.match(prefixRegex);
    return match ? true : false
  }

  // set default values for empty data in payload
  setDefaultPayloadValues(payload: any, item: string) {
    if (item) {
      const defaultPayloadValue = DEFAULT_PAYLOAD_VALUES[item];
      Object.keys(defaultPayloadValue).forEach(key => {
        const defaultVal = defaultPayloadValue[key];
        if (payload[key] == '' || typeof (payload[key]) == 'object' && !payload[key]?.id) {
          payload[key] = defaultVal;
        }
      })
    }
  }

  // transform empty object null data to "" in edit data
  transFormEditData(data: any) {
    Object.keys(data).forEach(key => {
      const value = data[key];
      if (value == null || typeof (data[key]) == 'object' && !data[key]?.id) {
        data[key] = '';
      }
    })
  }

  setDefaultPatchValues(data: any, item: string) {
    if (item) {
      const defaultValue = DEFAULT_PAYLOAD_VALUES[item];
      for (const key in defaultValue) {
        if (data[key] == null || !data[key]?.id) {
          data[key] = '';
        }
      }
    }
  }


  // expression evaluvation
  evaluateExpression(expression: string, context: Record<string, any>): string {
    // Create a function from the template string using new Function
    const fn = new Function(...Object.keys(context), `return \`${expression}\`;`);
    return fn(...Object.values(context)) ?? "";
  }

  // expression evaluvation
  evaluateExpression2(expression: string, context: Record<string, any>): string {
    // Create a function from the template string using new Function
    const fn = new Function(...Object.keys(context), `return (${expression});`);
    return fn(...Object.values(context)) ?? "";
  }


  getLayoutJSON(json_code: string, isMaster: boolean = false) {
    const data = InventoryLayoutJSON.find(json => json.json_code == json_code || json.json_type_id == json_code);
    return data ? this.deepCopy(isMaster ? data.layout_master : data.layout) : null;
  }

  getOtherLayoutJSON(json_code: string, layoutKey: string) {
    const data: any = InventoryLayoutJSON.find(json => json.json_code == json_code || json.json_type_id == json_code);
    return data?.[layoutKey] ?? null;
  }

  getValueByPath1(obj: any, path: string): any {

    // Normalize path like 'a.b[0].c' => ['a', 'b', '0', 'c']
    const parts = path.replace(/\[(\d+)\]/g, '.$1').split('.');

    return parts.reduce((acc, key) => {
      if (acc && typeof acc === 'object' && key in acc) {
        return acc[key];
      }
      return undefined;
    }, obj);
  }

  // Public function to get the value at the given path
  getValueByPath(obj: any, path: string): any {
    const pathParts = this.parsePath(path);
    return this.resolvePath(obj, pathParts);
  }

  // Parse path like 'b[1].c[*].d' into ['b', 1, 'c', '*', 'd']
  private parsePath(path: string): (string | number | '*')[] {
    const parts: (string | number | '*')[] = [];
    const regex = /([^[.\]]+)|\[(\*|\d+)\]/g;
    let match: RegExpExecArray | null;

    while ((match = regex.exec(path))) {
      if (match[1]) {
        parts.push(match[1]);
      } else if (match[2] === '*') {
        parts.push('*');
      } else {
        parts.push(parseInt(match[2], 10));
      }
    }

    return parts;
  }

  // Recursively resolve the path
  private resolvePath(current: any, pathParts: (string | number | '*')[]): any {
    if (pathParts.length === 0) return current;

    const [head, ...tail] = pathParts;

    if (head === '*') {
      if (!Array.isArray(current)) return undefined;
      const results = current.map(item => this.resolvePath(item, tail));
      return results.flat();
    }

    if (current == null) return undefined;

    return this.resolvePath(current[head], tail);
  }

  toBooleanSafe(value: unknown): boolean {
    return String(value).toLowerCase() === "true";
  }

  updateLayoutJsonValue(obj: any, data: any) {
    // } else if(data.value){
    // } else if(data.defaultValue) {
    data.value = this.getJSONValue(obj, data);
    if (data.transformAssignStorageData) {
      data.value = this.transformAssignStorageDataForInventory(data.value);
    }
    if (data.hideExpressionParams) {
      for (const key in data.hideExpressionParams) {
        const element = data.hideExpressionParams[key];
        data.hideExpressionParams[key] = this.getValueByPath(obj, element);
      }
      data.hide = this.toBooleanSafe(this.evaluateExpression(data.hideExpression, data.hideExpressionParams));
    }
  }

  getJSONValue(obj: any, data: any) {
    if (data.valueExpressionParams) {
      for (const key in data.valueExpressionParams) {
        const element = data.valueExpressionParams[key];
        data.valueExpressionParams[key] = this.getValueByPath(obj, element);
      }
      return data.isExpression2 ? this.evaluateExpression2(data.valueExpression, data.valueExpressionParams) : this.evaluateExpression(data.valueExpression, data.valueExpressionParams);
    } else if (data.value) {
      return this.getValueByPath(obj, data.value);
    } else if (data.defaultValue) {
      return data.defaultValue;
    }
    return '';
  }

  /**
 * Transforms the input array to join all 'assign_storage' into a single array
 * per sub_type_ids, moving the sub_type_ids as an object property.
 *
 * @param data The initial array of objects with 'sub_type_ids' and 'assign_storage'.
 * @returns An array of transformed objects.
 */
  transformAssignStorageDataForInventory(data: any[]): any[] {
    const transformedArray: any[] = [];
    if (data?.length) {
      for (const storage of data) {
        if (storage.box_plate_details?.length) {
          storage.box_plate_details.forEach((boxPlateDetail: any) => {
            const subItem = boxPlateDetail.sub_item;
            storage['sub_item'] = subItem;
            transformedArray.push(structuredClone(storage));
          });
        } else if (storage.sub_item) {
          const subItem = storage.sub_item;
          storage['sub_item'] = subItem;
          transformedArray.push(structuredClone(storage));
        }
      }
    }

    return transformedArray;
  }

  //returns object with patched data 
  getContextData(obj: any, data: any) {
    obj = structuredClone(obj);
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const element = obj[key];
        obj[key] = element ? this.getValueByPath(data, element) : null;
      }
    }
    return obj;
  }

  updateUrlPlaceHolders(url: string, data: any) {
    return url.replace(/{([^{}]+)}/g, (match, key) => {
      return data[key] !== undefined ? data[key] : match;
    });
  }

  // reset form and for formarry set inner form group count to 1
  resetForm(form: AbstractControl) {
    if (isFormGroup(form)) {
      Object.keys(form.controls).forEach(key => {
        const control = form.get(key);
        if (!control) return;
        this.resetForm(control);
      });
    } else if (isFormArray(form)) {
      this.clearFormArray(form);
      const control = form.controls.at(0);
      if (!control) return;
      this.resetForm(control);
    }
    form.reset();
  }

  clearFormArray(itemsArray: FormArray): void {
    while (itemsArray.length > 1) {
      itemsArray.removeAt(itemsArray.length - 1);
    }
    itemsArray.reset()
  }

  copyToClipBoard(data: string) {
    navigator.clipboard.writeText(data)
      .then(() => {
        this.toasterService.alert.toast({
          type: 'success',
          message: `${data?.length > 30 ? data.substring(0, 30) + '...' : data} copied to clipboard!`,
          delay: 2000
        });
      })
      .catch(err => {
        console.error('Could not copy text: ', err);
      });
  }

  splitFile(file: File | Blob, partSize: number): Blob[] {
    const parts: Blob[] = [];
    let start = 0;
    while (start < file.size) {
      const end = Math.min(start + partSize, file.size);
      const part = file.slice(start, end);
      parts.push(part);
      start = end;
    }
    return parts;
  }

  nullifyKeysWithIdZero(object: any) {
    if (typeof object !== 'object' || object === null) return;
    for (const key in object) {
      const value = object[key];
      if (
        typeof value === 'object' &&
        value !== null
      ) {
        if ('id' in value && value.id === 0) {
          object[key] = null;
        } else {
          this.nullifyKeysWithIdZero(value); // recursive call
        }
      }
    }
    return object;
  }

  syncNestedRowHeights(nestedTables: ElementRef<any>[]) {

    if (nestedTables.length === 0) return;

    const rowCount = nestedTables[0].nativeElement.rows.length;

    for (let i = 0; i < rowCount; i++) {
      let maxHeight = 0;

      for (const table of nestedTables) {
        const nestedRow = (table as any).nativeElement.rows[i];
        if (nestedRow) {
          const height = nestedRow.getBoundingClientRect().height;
          if (height > maxHeight) maxHeight = height;
        }
      }

      for (const table of nestedTables) {
        const nestedRow = (table as any).nativeElement.rows[i];
        if (nestedRow) {
          nestedRow.style.height = maxHeight + 'px';
        }
      }
    }
  }
  scrollDivOnClick(scrollType: string, scrollAmount: number, scrollItem: HTMLElement) {
    if (scrollItem && !this.isScrollable(scrollItem, scrollType)) {
      if (scrollType == "next") {
        scrollItem.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      } else {
        scrollItem.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
      }
    }
  }

  isScrollable(scrollItem: HTMLElement, scrollType: string) {
    const isAtStart = scrollItem.scrollLeft === 0;
    const isAtEnd = scrollItem.scrollLeft + scrollItem.clientWidth >= scrollItem.scrollWidth;
    return (isAtStart && scrollType == "previous") || (isAtEnd && scrollType == "next")
  }

  // updates target keys values
  updateObjectKeyValue<T>(obj: T, targetKey: string, newValue: any): T {
    if (obj === null || typeof obj !== 'object') {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map((item) => this.updateObjectKeyValue(item, targetKey, newValue)) as unknown as T;
    }

    return Object.keys(obj).reduce((acc: any, key) => {
      if (key === targetKey) {
        acc[key] = newValue;
      } else {
        acc[key] = this.updateObjectKeyValue((obj as any)[key], targetKey, newValue);
      }
      return acc;
    }, {});
  }

  updateInventoryUrl(url: string) {

    // match {item_type=some_key} 
    const regex = /\{item_type=([\w-]+)\}/;

    const match = url.match(regex);
    if (!match) {
      return url;
    }

    const key = match[1];

    // check key exists in inventoryTypeIds
    if (Object.hasOwn(this.inventoryTypeIds, key)) {
      url = url.replace(regex, this.inventoryTypeIds[key].toString());
    }

    return url;

  }

  removeAdditionalKeys(payloadSchema: any): any {
    if (Array.isArray(payloadSchema)) {
      return payloadSchema.map((item: any) => this.removeAdditionalKeys(item))
    }
    if (payloadSchema !== null && typeof payloadSchema === 'object') {
      const result: any = {};
      for (const [key, value] of Object.entries(payloadSchema)) {
        if (!key.endsWith('__')) {
          result[key] = this.removeAdditionalKeys(value);
        }
      }
      return result;
    }

    return payloadSchema;
  }

  //returns put method to upload file to s3 directly
  uploadFileToS3Api( url: string, file: File | Blob, headers: HttpHeaders) {
    // uncomment to resolve local cros-orgin issue;

    // const s3url = APP_CONSTANTS.S3_URL;
    // if (url.includes(APP_CONSTANTS.S3_UPLOAD)) {
    //   url = url.replace(s3url, "s3upload");
    // }

    return this.http.put(url, file, {
      headers: headers,
      reportProgress: true,
      observe: 'events'
    });
  }

}
